import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const STAGE_ORDER = ["Lead", "Qualified", "Proposal", "Negotiation", "Won", "Lost"];
const STAGE_SCORE = Object.fromEntries(STAGE_ORDER.map((s, i) => [s, i]));

function bestStage(stages) {
  if (!stages.length) return "—";
  return stages.reduce((best, s) =>
    (STAGE_SCORE[s] ?? -1) > (STAGE_SCORE[best] ?? -1) ? s : best
  );
}

const STAGE_COLOR = {
  Lead: "#475569",
  Qualified: "#0b72b9",
  Proposal: "#b45309",
  Negotiation: "#6d28d9",
  Won: "#15803d",
  Lost: "#b91c1c",
};

function buildContacts(deals) {
  const map = new Map();

  for (const deal of deals) {
    const email = (deal.contactEmail || "").trim().toLowerCase();
    const name = (deal.contactName || "").trim();
    const key = email || name.toLowerCase();
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, {
        name,
        email: deal.contactEmail?.trim() || "",
        phone: deal.contactPhone?.trim() || "",
        companies: new Set(),
        stages: [],
        totalDeals: 0,
      });
    }

    const contact = map.get(key);
    if (deal.accountName?.trim()) contact.companies.add(deal.accountName.trim());
    contact.stages.push(deal.stage);
    contact.totalDeals += 1;

    // Keep the most populated contact info
    if (!contact.phone && deal.contactPhone?.trim()) contact.phone = deal.contactPhone.trim();
    if (!contact.email && deal.contactEmail?.trim()) contact.email = deal.contactEmail.trim();
  }

  return Array.from(map.values())
    .map((c) => ({
      ...c,
      company: Array.from(c.companies).join(", ") || "—",
      stage: bestStage(c.stages),
    }))
    .sort((a, b) => b.totalDeals - a.totalDeals);
}

export default function ContactsPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/crm/deals");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load contacts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const contacts = useMemo(() => buildContacts(deals), [deals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.company.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle="People and decision-makers across your accounts."
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or company"
            aria-label="Search contacts"
          />
        </div>
        {!loading && (
          <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            {filtered.length} contact{filtered.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "24px 20px" }}>
            {[1, 2, 3, 4].map((i) => (
              <Loader key={i} type="skeleton" height={44} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              title={search ? "No contacts match your search" : "No contacts yet"}
              subtitle={search ? "Try a different name or email." : "Add deals with contact details in the Pipeline to see contacts here."}
            />
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["Name", "Company", "Email", "Phone", "Deals", "Best Stage", ""].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: "10px 16px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text-2)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((contact, idx) => (
                <tr
                  key={`${contact.name}-${idx}`}
                  style={{ borderBottom: "1px solid var(--border)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600, fontSize: 14 }}>
                    {contact.name || "—"}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text-2)" }}>
                    {contact.company}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} style={{ color: "var(--brand)", textDecoration: "none" }}>
                        {contact.email}
                      </a>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text-2)" }}>
                    {contact.phone || "—"}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-2)" }}>
                    {contact.totalDeals}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {contact.stage !== "—" ? (
                      <span style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: STAGE_COLOR[contact.stage] || "var(--text-2)",
                        background: `${STAGE_COLOR[contact.stage]}18` || "var(--surface-2)",
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: `1px solid ${STAGE_COLOR[contact.stage]}40`,
                      }}>
                        {contact.stage}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 12, whiteSpace: "nowrap" }}
                      onClick={() => navigate("/crm/pipeline", { state: { search: contact.name } })}
                    >
                      View Deals
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
